import { useRef, useState } from "react";
import { describeError } from "../../api/client";
import type { AdminCampaignSummary } from "../../api/types";
import { isSelectedOutsideWindow } from "../campaigns/selectorFiltering";
import { listCampaigns } from "../campaigns/api";
import { Combobox, type ComboboxOption } from "../../components/Combobox";
import { FeedbackPanel } from "../../components/FeedbackPanel";
import { formatDateTime } from "../../formatting/date";
import { useQuery } from "../../state/useQuery";

/** The Panel API clamps `size` to 50 (`AdminCampaignSqlBuilder.NormalizeSize`). */
const PAGE_SIZE = 50;

/**
 * Recognizable-by-name campaign selector for the standalone `/telemetria`
 * route (design.md §5.6: "`/telemetria` uses a campaign selector with name,
 * date, and state"; task 10.5/10.6). Reuses the EXISTING `GET panel/campaigns`
 * list (page/size-backed, same as `CampaignListScreen`).
 *
 * Panel feedback 2026-10-30 (T4): the header picker is a searchable
 * `Combobox` (portal popover, never clipped). The Anterior/Siguiente pager
 * that issue #174 introduced to make every campaign reachable is gone from
 * the header: the list API has no search parameter (only `estado`/`page`/
 * `size`), so the options are loaded page by page — the combobox filters the
 * campaigns loaded so far client-side and offers "Cargar más" to pull the next
 * page of 50 into the SAME list (pages accumulate; nothing is replaced).
 *
 * Codex review on PR #181/#184, still honored:
 * - The externally-controlled `value` (e.g. `?campaignId=`) can name a campaign
 *   that is not among the loaded ones — the same "assigned value falls outside
 *   the bounded window" problem `TemplateSelector` solves with
 *   `isSelectedOutsideWindow`; it is kept visible as a disabled placeholder.
 * - A failed fetch of a further page keeps the already-loaded campaigns usable
 *   and surfaces `stale` with a `Reintentar` panel (kept mounted during the
 *   retry so keyboard focus is never dropped; its message becomes
 *   "Reintentando…").
 * - When the very first list request fails, the all-campaigns option (when
 *   `allOptionLabel` is provided) must stay reachable so the operator can
 *   return to the account-wide view (Codex PR #385 round 3).
 */
export function CampaignSelector({
  id,
  value,
  onChange,
  allOptionLabel,
  compact,
}: {
  id: string;
  value: string;
  onChange: (campaignId: string) => void;
  /**
   * REDISENO-PANEL.md §6: TelemetryScreen's header needs a SELECTABLE
   * "Todas las campañas" option (value `""`) ahead of the real campaign
   * list. Omitted (or empty) renders the plain "Selecciona una campaña"
   * placeholder instead (no selectable empty option).
   */
  allOptionLabel?: string;
  /**
   * REDISENO-PANEL.md §6: drops the bordered/shadowed card chrome and
   * visually hides the "Campaña" label text (kept for assistive tech via the
   * same `htmlFor`/`id` pairing) — the header's own H1 already names the
   * screen.
   */
  compact?: boolean;
}) {
  const [page, setPage] = useState(1);
  const { data, error, loading, refreshing, stale, updatedAt, refetch } = useQuery({
    key: ["campaign-options", page, PAGE_SIZE],
    queryFn: () => listCampaigns({ page, size: PAGE_SIZE }),
  });

  // Pages accumulate by the page number the SERVER reports for them, so a
  // stale previous page's data (kept by `useQuery` while the next page loads)
  // is never filed under the newly-requested page number.
  const loadedPages = useRef(new Map<number, AdminCampaignSummary[]>());
  if (data) loadedPages.current.set(data.page, data.items);
  const pageNumbers = [...loadedPages.current.keys()].sort((a, b) => a - b);
  const items = pageNumbers.flatMap((pageNumber) => loadedPages.current.get(pageNumber) ?? []);
  const lastLoadedPage = pageNumbers.length > 0 ? pageNumbers[pageNumbers.length - 1]! : 0;
  const hasMore = data !== undefined && lastLoadedPage < Math.ceil(data.total / PAGE_SIZE);

  const selectedOutsideWindow = isSelectedOutsideWindow(value, data ? items : undefined, (option) => option.campaignId);
  const hasAllOption = Boolean(allOptionLabel);

  const options: ComboboxOption[] = [
    ...(hasAllOption ? [{ value: "", label: allOptionLabel as string }] : []),
    ...(!data && value !== ""
      ? [{ value, label: "Campaña seleccionada (no se pudo cargar la lista)", disabled: true }]
      : []),
    ...(selectedOutsideWindow
      ? [{ value, label: "Campaña seleccionada (no está en la lista cargada)", disabled: true }]
      : []),
    ...items.map((option) => {
      // Round-2 client decision (prototype telOpts): the option label is the
      // campaign name only; date and estado live in the screen subtitle.
      return { value: option.campaignId, label: option.nombre };
    }),
  ];

  const showCombobox = data ? !(items.length === 0 && !selectedOutsideWindow && !hasAllOption) : hasAllOption;

  return (
    <div className={compact ? "v2-campaign-selector v2-campaign-selector--compact" : "v2-campaign-selector"}>
      {loading && <p role="status">Cargando campañas…</p>}

      {!data && error !== undefined && (
        <FeedbackPanel
          variant="error"
          message={describeError(error, "No se pudieron cargar las campañas.")}
          onRetry={refetch}
        />
      )}

      {/* `runFetch` only flips `phase` to "loading" on retry and leaves `stale`
          at its prior value until the retry settles (state/useQuery.ts), so
          this panel stays mounted across the retry (focus is never dropped)
          and its own message switches instead of a second paragraph. */}
      {data && stale && (
        <FeedbackPanel
          variant="stale"
          message={
            refreshing
              ? "Reintentando…"
              : `No se pudo cargar más campañas; mostrando resultados anteriores${
                  updatedAt ? ` · ${formatDateTime(new Date(updatedAt).toISOString())}` : ""
                }`
          }
          onRetry={refetch}
        />
      )}

      {data && !showCombobox && <FeedbackPanel variant="empty" message="No hay campañas disponibles." />}

      {showCombobox && (
        <div className="v2-campaign-selector__field">
          <label htmlFor={id} className={compact ? "v2-u-visually-hidden" : undefined}>
            Campaña
          </label>
          <Combobox
            id={id}
            value={value}
            onChange={onChange}
            options={options}
            placeholder="Selecciona una campaña"
            hasMore={hasMore}
            loadingMore={refreshing && !stale}
            onLoadMore={() => setPage(lastLoadedPage + 1)}
            loadingLabel="Cargando campañas…"
          />
        </div>
      )}

      {selectedOutsideWindow && (
        <p className="v2-campaign-selector__outside-window">
          La campaña seleccionada no está entre las cargadas; usa «Cargar más» o busca por nombre.
        </p>
      )}
    </div>
  );
}
